import { captureException } from '@sentry/react';

/** Re-report a sustained wall once it crosses this consecutive-hit count (in addition to entry). */
const SUSTAINED_THRESHOLD = 10;

/**
 * Whether to notify Sentry for this hit. We report once when a host first enters backoff
 * (consecutive === 1) and once more if the wall is sustained, so a long 429 storm produces a
 * couple of meaningful alerts rather than one per retry.
 */
export const shouldReportRateLimit = (consecutive: number): boolean =>
  consecutive === 1 || consecutive === SUSTAINED_THRESHOLD;

type RateLimitReport = {
  backoffMs: number;
  consecutive: number;
  customerId?: string;
  retryAfter?: string | number | null;
  source: 'callApi' | 'callInterval';
  status?: number;
  url: string;
};

/**
 * Report an API rate-limit / server-error wall to Sentry with debugging context.
 * `captureException` (over `captureMessage`) preserves the Axios error + stack.
 */
export const reportRateLimit = (error: Error, ctx: RateLimitReport): void => {
  captureException(error, {
    tags: {
      errorType: 'rate-limit',
      feature: 'api-client-backoff',
      status: ctx.status ?? 'unknown',
    },
    contexts: {
      rateLimit: {
        backoffMs: ctx.backoffMs,
        consecutive: ctx.consecutive,
        customerId: ctx.customerId ?? null,
        retryAfter: ctx.retryAfter ?? null,
        source: ctx.source,
        status: ctx.status ?? null,
        url: ctx.url,
      },
    },
  });
};
